import { useTranslation } from 'react-i18next';

import { Spinner } from '../../components';
import { ResultsCardFrame } from '../results/ResultsCardFrame';
import type { ResultSession } from '../results/sessions';
import { useResultSessionStore } from '../results/store';
import type { BoardContextValue } from '../useBoardContext';
import { ExamDetailTab } from './ExamDetailTab';
import { ExamStatsTab } from './ExamStatsTab';
import { IZ_SIGNED_OUT } from './classExams';
import { examDisplayTitle } from './examResults';
import { useExamResultStore } from './store';
import { useClassExamLoad, useExamResultsView } from './useExamResults';
import { useMsg } from '../../i18n/msg';

export interface ExamResultsCardProps {
  session: ResultSession;
  /** No rail for this tab, so the card carries its own Báo cáo | Chi tiết switch. */
  switchable?: boolean;
  context: BoardContextValue;
  onBackToDe: () => void;
}

// Real class results for the exam on this tab
export function ExamResultsCard({ session, context, switchable = false, onBackToDe }: ExamResultsCardProps) {
  const examId = session.refId;
  const { t } = useTranslation('exams');
  const tr = useMsg();
  useClassExamLoad(context);
  const { view, exam, results, names, attemptsLoading, questionsLoading, questionsError, retryQuestions, retryAttempts } = useExamResultsView(examId);
  const titles = useExamResultStore((state) => state.courseTitles);
  const examTitles = useExamResultStore((state) => state.examTitles);
  const setPanelTab = useResultSessionStore((state) => state.setPanelTab);
  const setCollapsed = useResultSessionStore((state) => state.setCollapsed);
  const hide = useResultSessionStore((state) => state.hide);
  const openStudent = useResultSessionStore((state) => state.openStudent);

  const subtitle = exam ? [examDisplayTitle(exam, examTitles), titles[exam.courseId] || ''].filter(Boolean).join(' • ') : tr(session.title);
  const questionNote = questionsLoading ? <Spinner size="small" label={t('results.loadingQuestions')} /> : questionsError
    ? <p className="quiz-results__empty">{t('results.questionsFailed', { error: tr(questionsError) })} <button type="button" className="hw-panel__refresh" onClick={retryQuestions}>{t('actions.retry')}</button></p>
    : null;
  const actions = session.student ? <button type="button" className="hw-panel__refresh" onClick={onBackToDe}>{t('actions.backToDe')}</button> : null;
  const onOpenStudent = (code: string) => openStudent(session.tabId, code);
  const attempt = exam && session.student ? exam.byCode[session.student] : undefined;
  const studentName = session.student ? names[session.student] || (attempt ? attempt.name : session.student) : null;

  return (
    <ResultsCardFrame kindLabel={t('results.kindExam')} subtitle={subtitle} actions={actions} view={session.panelTab} onView={switchable ? (view) => setPanelTab(session.tabId, view) : undefined}
      collapsed={session.collapsed} studentName={studentName} onCollapse={() => setCollapsed(session.tabId, true)} onExpand={() => setCollapsed(session.tabId, false)} onClose={() => hide(session.tabId)}>
      {!results ? (view.kind === 'error' ? <p className="quiz-results__empty">{t('results.resultsFailed', { error: view.message === IZ_SIGNED_OUT ? t('errors.izSignedOut') : tr(view.message) })} <button type="button" className="hw-panel__refresh" onClick={retryAttempts}>{t('actions.retry')}</button></p> : <Spinner label={t('results.loadingResults')} />)
        : session.panelTab === 'stats' ? <ExamStatsTab results={results} questionNote={questionNote} />
          : <ExamDetailTab results={results} activeCode={session.student} onOpenStudent={onOpenStudent} onReload={retryAttempts} reloading={attemptsLoading} />}
    </ResultsCardFrame>
  );
}
